import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { StateView } from '@/card/record/StateView'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import type { Locale } from '@/i18n'
import { formatNumber } from '@/lib/format'
import { Pager, useClientPages } from '@/lists/Pager'
import { fetchOrganizations } from '@/payments/api'
import { fetchCashFlow, fetchCashFlowForecast, fetchReceivables } from './api'
import {
  Money,
  moneyText,
  num,
  percentText,
  PeriodBar,
  periodLabel,
  periodOf,
  QueryState,
  singleCurrencyTotal,
  SkeletonLines,
  type StepPeriod,
} from './reportKit'
import { iso } from './types'
import type { AgeingLine, CashFlowForecastLine, CashFlowLine } from './types'

/**
 * Финансовые отчёты: дебиторка по срокам и движение денег (FinanceReports.dc.html; ТЗ §4.6).
 *
 * ## Дебиторка: просрочка отдельно от ещё не наступившего срока
 *
 * Счёт, выставленный вчера со сроком через месяц, — не долговая проблема. «Не наступил
 * срок» стоит своей корзиной и не входит в «просрочено». Имена полей — серверные
 * (`bucket0to30…bucket90plus`, `overdueTotal`), иначе экран молча показывает пустоту.
 *
 * ## ДДС: факт и прогноз — разные ряды
 *
 * Факт — что прошло по счетам, прогноз — что ожидается. На графике и в таблицах это разные
 * ряды; прогноз — двумя суммами, сырой («сколько может прийти») и взвешенной («на сколько
 * планировать»). Свести их в одну колонку значило бы поставить воронку рядом с выпиской.
 *
 * ## Чья организация
 *
 * ДДС считается по организации, и экран спрашивает, по какой: единственная выбирается сама,
 * из нескольких выбирает человек. Первая попавшаяся показала бы чужие деньги.
 *
 * ## Чего нет из макета
 *
 * Недельного ряда остатка, неснижаемого остатка и платёжного календаря (сервер отдаёт суммы
 * за период по статьям и источникам, без остатка и без недель), числа счетов в корзине,
 * максимальной просрочки, ответственного и стадии взыскания по должнику, экспорта.
 */

const BUCKETS = [
  { key: 'notYetDue', label: 'fr.bucket.notYetDue', color: 'var(--seq-0)', overdue: false },
  { key: 'bucket0to30', label: 'report.ageing.b0', color: 'var(--seq-1)', overdue: true },
  { key: 'bucket31to60', label: 'report.ageing.b31', color: 'var(--seq-2)', overdue: true },
  { key: 'bucket61to90', label: 'report.ageing.b61', color: 'var(--seq-3)', overdue: true },
  { key: 'bucket90plus', label: 'report.ageing.b90', color: 'var(--seq-4)', overdue: true },
] as const

type BucketKey = (typeof BUCKETS)[number]['key']

export function FinanceReportsPage() {
  const { t, user } = useApp()
  const [period, setPeriod] = useState<StepPeriod>(() => periodOf('month', new Date()))
  const [organizationId, setOrganizationId] = useState<string | null>(null)

  const mayReceivables = can(user, 'billing.invoice', 'read')
  const mayLedger = can(user, 'payments.ledger', 'read')

  return (
    <div className="stack" style={{ gap: 14 }}>
      <nav className="crumbs" aria-label={t('record.crumbs')}>
        <span>{t('nav.section.analytics')}</span>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{t('nav.financeReports')}</span>
      </nav>
      <div className="page-head">
        <h1 className="h1">{t('nav.financeReports')}</h1>
      </div>
      <p className="page-lead">{t('fr.lead')}</p>

      <Receivables allowed={mayReceivables} />

      <section className="panel" aria-labelledby="fr-dds">
        <div className="panel-h" style={{ flexWrap: 'wrap', gap: 8 }}>
          <span className="h2" id="fr-dds">{t('fr.dds.title')}</span>
          <span className="spacer" />
          {mayLedger && (
            <>
              <OrganizationSeg value={organizationId} onChange={setOrganizationId} />
              <PeriodBar value={period} onChange={setPeriod} />
            </>
          )}
        </div>
        {!mayLedger ? (
          <StateView kind="forbidden" compact title={t('fr.dds.forbidden')} description={t('state.forbidden.text')} />
        ) : !organizationId ? (
          <StateView kind="setup" compact title={t('fr.dds.chooseOrg')} description={t('fr.dds.chooseOrgText')} />
        ) : (
          <CashFlow organizationId={organizationId} period={period} />
        )}
      </section>
    </div>
  )
}

// ------------------------------------------------------------------ дебиторка

function Receivables({ allowed }: { allowed: boolean }) {
  const { t, locale } = useApp()
  const lines = useQuery({
    queryKey: ['receivables'],
    enabled: allowed,
    queryFn: ({ signal }) => fetchReceivables(signal),
    retry: false,
  })
  const data = lines.data ?? []
  const totalAll = singleCurrencyTotal(data, (l) => l.outstandingTotal, (l) => l.currency)
  const bucketTotal = (key: BucketKey) => singleCurrencyTotal(data, (l) => l[key], (l) => l.currency)
  const overdue = singleCurrencyTotal(data, (l) => l.overdueTotal, (l) => l.currency)
  const debtorsOverdue = data.filter((l) => (num(l.overdueTotal) ?? 0) > 0)
  const debtors = debtorsOverdue.slice().sort((a, b) => (num(b.overdueTotal) ?? 0) - (num(a.overdueTotal) ?? 0))
  const { visible, pager } = useClientPages(debtors, 25)
  const currency = totalAll?.currency ?? null
  const share = (amount: number | null) => (totalAll && totalAll.amount > 0 && amount != null ? (amount / totalAll.amount) * 100 : null)

  return (
    <section className="panel" aria-labelledby="fr-ar">
      <div className="panel-h">
        <span className="h2" id="fr-ar">{t('fr.ar.title')}</span>
        {lines.isSuccess && <span className="count">{t('fr.ar.count', { count: data.length })}</span>}
        <span className="spacer" />
        {allowed && <Link className="btn btn-ghost btn-sm" to="/invoices">{t('fr.ar.openInvoices')}</Link>}
      </div>
      {!allowed ? (
        <StateView kind="forbidden" compact title={t('fr.ar.forbidden')} description={t('state.forbidden.text')} />
      ) : lines.isLoading ? (
        <SkeletonLines rows={6} label={t('fr.loading')} />
      ) : lines.error ? (
        <QueryState compact error={lines.error} errorTitle={t('fr.ar.error')} onRetry={() => void lines.refetch()} />
      ) : data.length === 0 ? (
        <StateView kind="empty" compact title={t('fr.ar.empty')} description={t('fr.ar.emptyText')} />
      ) : (
        <>
          {totalAll == null && (
            <div className="panel-b" style={{ paddingBottom: 0 }}>
              <div className="alert alert-warning" role="status">
                <Icon name="alert" />
                <span className="fg">{t('fr.ar.mixedCurrency')}</span>
              </div>
            </div>
          )}
          {totalAll && totalAll.amount > 0 && (
            <div className="panel-b stack" style={{ gap: 10 }}>
              <div
                className="stackbar"
                role="img"
                aria-label={BUCKETS.map((b) => `${t(b.label)} ${moneyText(bucketTotal(b.key)?.amount ?? null, currency, locale)}`).join('; ')}
              >
                {BUCKETS.map((bucket) => (
                  <i key={bucket.key} style={{ flex: bucketTotal(bucket.key)?.amount ?? 0, background: bucket.color }} />
                ))}
              </div>
              <div className="row t-sm secondary" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <span>
                  {t('fr.bucket.notYetDue')} —{' '}
                  <span className="strong" style={{ color: 'var(--fg)' }}>
                    {moneyText(bucketTotal('notYetDue')?.amount ?? null, currency, locale)} · {percentText(share(bucketTotal('notYetDue')?.amount ?? null), locale)}
                  </span>
                </span>
                {overdue && overdue.amount > 0 && (
                  <span className="flag flag-danger">
                    <Icon name="clock" />
                    {t('fr.ar.overdueFlag', {
                      sum: moneyText(overdue.amount, overdue.currency, locale),
                      pct: percentText(share(overdue.amount), locale),
                      count: debtorsOverdue.length,
                    })}
                  </span>
                )}
              </div>
            </div>
          )}
          <table className="tbl" aria-label={t('fr.ar.buckets')}>
            <thead>
              <tr>
                <th>{t('fr.ar.term')}</th>
                <th style={{ width: '34%' }}>{t('fr.ar.shareBar')}</th>
                <th className="num">{t('report.cash.amount')}</th>
                <th className="num">{t('fr.ar.share')}</th>
                <th className="num">{t('fr.ar.clients')}</th>
              </tr>
            </thead>
            <tbody>
              {BUCKETS.map((bucket) => {
                const amount = bucketTotal(bucket.key)?.amount ?? null
                const pct = share(amount)
                return (
                  <tr key={bucket.key}>
                    <td>
                      <span className="row" style={{ gap: 8 }}>
                        <span className="legend-sw" style={{ background: bucket.color }} />
                        <span className={bucket.overdue && bucket.key !== 'bucket0to30' ? 'strong' : undefined}>{t(bucket.label)}</span>
                      </span>
                    </td>
                    <td>
                      <span className="hbar" style={{ display: 'block', width: `${Math.max(0, Math.min(100, pct ?? 0))}%`, background: bucket.color }} />
                    </td>
                    <td className="num strong">{totalAll ? moneyText(amount, currency, locale) : '—'}</td>
                    <td className="num">{percentText(pct, locale)}</td>
                    <td className="num">{formatNumber(data.filter((l) => (num(l[bucket.key]) ?? 0) > 0).length, locale)}</td>
                  </tr>
                )
              })}
              <tr className="total">
                <td>{t('sr.total')}</td>
                <td className="t-sm muted" style={{ fontWeight: 400 }}>{t('fr.ar.totalNote')}</td>
                <td className="num">{totalAll ? moneyText(totalAll.amount, currency, locale) : '—'}</td>
                <td className="num">{totalAll ? '100 %' : '—'}</td>
                <td className="num">{formatNumber(data.length, locale)}</td>
              </tr>
            </tbody>
          </table>

          <div className="list-sub" style={{ borderTop: '1px solid var(--border)' }}>{t('fr.ar.debtors')}</div>
          {debtors.length === 0 ? (
            <div className="panel-b secondary">{t('fr.ar.noOverdue')}</div>
          ) : (
            <>
              <div className="wa-scroll">
                <table className="tbl" aria-label={t('fr.ar.debtors')}>
                  <thead>
                    <tr>
                      <th>{t('report.ageing.counterparty')}</th>
                      <th className="num">{t('fr.ar.debtTotal')}</th>
                      <th className="num">{t('report.ageing.overdue')}</th>
                      <th className="num">{t('report.ageing.b0')}</th>
                      <th className="num">{t('report.ageing.b31')}</th>
                      <th className="num">{t('report.ageing.b61')}</th>
                      <th className="num">{t('report.ageing.b90')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((line) => (
                      <DebtorRow key={line.counterpartyId} line={line} />
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager {...pager} />
            </>
          )}
        </>
      )}
    </section>
  )
}

function DebtorRow({ line }: { line: AgeingLine }) {
  return (
    <tr>
      <td>
        <span className="stack" style={{ gap: 0 }}>
          <Link to={`/counterparties/${line.counterpartyId}`}>{line.counterpartyName ?? line.inn ?? '—'}</Link>
          {line.inn && <span className="mono t-xs muted">{line.inn}</span>}
        </span>
      </td>
      <td className="num"><Money value={line.outstandingTotal} currency={line.currency} /></td>
      <td className="num strong"><Money value={line.overdueTotal} currency={line.currency} /></td>
      <td className="num"><Money value={line.bucket0to30} currency={line.currency} /></td>
      <td className="num"><Money value={line.bucket31to60} currency={line.currency} /></td>
      <td className="num"><Money value={line.bucket61to90} currency={line.currency} /></td>
      <td className="num"><Money value={line.bucket90plus} currency={line.currency} /></td>
    </tr>
  )
}

// ------------------------------------------------------------------ ДДС

function OrganizationSeg({ value, onChange }: { value: string | null; onChange: (id: string) => void }) {
  const { t } = useApp()
  const organizations = useQuery({
    queryKey: ['payment-organizations'],
    queryFn: ({ signal }) => fetchOrganizations(signal),
    retry: false,
  })
  const list = organizations.data ?? []
  // Единственная выбирается сама; из нескольких выбирает человек.
  useEffect(() => {
    const only = list.length === 1 ? list[0] : undefined
    if (!value && only) onChange(only.id)
  }, [value, list, onChange])

  if (organizations.error) {
    return (
      <span className="t-sm muted">
        {organizations.error instanceof ApiError && organizations.error.isForbidden ? t('fr.org.forbidden') : t('fr.org.error')}
      </span>
    )
  }
  if (list.length <= 1) return list[0] ? <span className="t-sm secondary">{list[0].name}</span> : null
  return (
    <div className="seg" role="group" aria-label={t('payments.organization')}>
      {list.map((organization) => (
        <button key={organization.id} type="button" aria-pressed={value === organization.id} onClick={() => onChange(organization.id)}>
          {organization.name}
        </button>
      ))}
    </div>
  )
}

function CashFlow({ organizationId, period }: { organizationId: string; period: StepPeriod }) {
  const { t, locale } = useApp()
  const today = new Date()
  const horizon = { from: iso(today), to: iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 42)) }
  const actual = useQuery({
    queryKey: ['cash-flow', organizationId, period.from, period.to],
    queryFn: ({ signal }) => fetchCashFlow({ from: period.from, to: period.to }, signal, organizationId),
    retry: false,
  })
  const forecast = useQuery({
    queryKey: ['cash-flow-forecast', organizationId, horizon.from, horizon.to],
    queryFn: ({ signal }) => fetchCashFlowForecast(horizon, signal, organizationId),
    retry: false,
  })

  const fact = actual.data ?? []
  const plan = forecast.data ?? []
  const incoming = (direction: string) => direction === 'incoming' || direction === 'in'
  const factIn = singleCurrencyTotal(fact.filter((l) => incoming(l.direction)), (l) => l.amount, (l) => l.currency)
  const factOut = singleCurrencyTotal(fact.filter((l) => !incoming(l.direction)), (l) => l.amount, (l) => l.currency)
  const rawIn = singleCurrencyTotal(plan.filter((l) => incoming(l.direction)), (l) => l.amount, (l) => l.currency)
  const rawOut = singleCurrencyTotal(plan.filter((l) => !incoming(l.direction)), (l) => l.amount, (l) => l.currency)
  const wIn = singleCurrencyTotal(plan.filter((l) => incoming(l.direction)), (l) => l.weightedAmount, (l) => l.currency)
  const wOut = singleCurrencyTotal(plan.filter((l) => !incoming(l.direction)), (l) => l.weightedAmount, (l) => l.currency)
  const currency = factIn?.currency ?? factOut?.currency ?? rawIn?.currency ?? rawOut?.currency ?? null

  if (actual.isLoading || forecast.isLoading) return <SkeletonLines rows={6} label={t('fr.loading')} />

  return (
    <>
      <div className="panel-b stack" style={{ gap: 12 }}>
        <div className="legend" aria-label={t('fr.dds.legend')}>
          <span><span className="legend-sw" style={{ background: 'var(--accent)', marginRight: 6 }} />{t('fr.dds.fact', { period: periodLabel(period, t, locale) })}</span>
          <span><span className="ln fc" />{t('fr.dds.raw')}</span>
          <span><span className="ln fcw" />{t('fr.dds.weighted')}</span>
        </div>
        {actual.error || forecast.error ? null : (
          <FlowChart
            currency={currency}
            locale={locale}
            groups={[
              { label: t('report.cash.direction.incoming'), fact: factIn?.amount ?? 0, raw: rawIn?.amount ?? 0, weighted: wIn?.amount ?? 0 },
              { label: t('report.cash.direction.outgoing'), fact: factOut?.amount ?? 0, raw: rawOut?.amount ?? 0, weighted: wOut?.amount ?? 0 },
            ]}
          />
        )}
      </div>

      <div className="wa-grid" style={{ padding: '0 14px 14px' }}>
        <div className="stack" style={{ gap: 6 }}>
          <span className="h3">{t('fr.dds.factTitle', { period: periodLabel(period, t, locale) })}</span>
          {actual.error ? (
            <QueryState compact error={actual.error} errorTitle={t('fr.dds.factError')} onRetry={() => void actual.refetch()} />
          ) : fact.length === 0 ? (
            <StateView kind="empty" compact title={t('fr.dds.factEmpty')} />
          ) : (
            <FactTable lines={fact} />
          )}
        </div>
        <div className="stack" style={{ gap: 6 }}>
          <span className="h3">{t('fr.dds.planTitle')}</span>
          {forecast.error ? (
            <QueryState compact error={forecast.error} errorTitle={t('fr.dds.planError')} onRetry={() => void forecast.refetch()} />
          ) : plan.length === 0 ? (
            <StateView kind="empty" compact title={t('fr.dds.planEmpty')} />
          ) : (
            <ForecastTable lines={plan} />
          )}
        </div>
      </div>
    </>
  )
}

function FactTable({ lines }: { lines: CashFlowLine[] }) {
  const { t } = useApp()
  return (
    <div className="tbl-wrap">
      <table className="tbl" aria-label={t('fr.dds.factTable')}>
        <thead>
          <tr>
            <th>{t('report.cash.item')}</th>
            <th>{t('report.cash.direction')}</th>
            <th className="num">{t('report.cash.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${line.itemCode}-${line.direction}-${index}`}>
              <td>{line.itemName ?? line.itemCode ?? '—'}</td>
              <td>{t(`report.cash.direction.${line.direction}`)}</td>
              <td className="num"><Money value={line.amount} currency={line.currency} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ForecastTable({ lines }: { lines: CashFlowForecastLine[] }) {
  const { t } = useApp()
  return (
    <div className="tbl-wrap">
      <table className="tbl" aria-label={t('fr.dds.planTable')}>
        <thead>
          <tr>
            <th>{t('report.cash.source')}</th>
            <th>{t('report.cash.direction')}</th>
            {/* Обе суммы: «сколько может прийти» и «на сколько планировать» — разные вопросы. */}
            <th className="num">{t('fr.dds.rawCol')}</th>
            <th className="num">{t('fr.dds.weightedCol')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${line.sourceKind}-${line.direction}-${index}`}>
              <td>{t(`report.cash.kind.${line.sourceKind}`)}</td>
              <td>{t(`report.cash.direction.${line.direction}`)}</td>
              <td className="num secondary"><Money value={line.amount} currency={line.currency} /></td>
              <td className="num strong"><Money value={line.weightedAmount} currency={line.currency} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Столбцы «факт · прогноз сырой · прогноз взвешенный» по поступлениям и выплатам.
 *
 * Три ряда рисуются разными знаками (сплошной, пунктир, точки), а не одним цветом разной
 * яркости: прогноз не должен читаться как часть факта. Высота по сумме — только при одной
 * валюте; итоги по смешанным валютам не складываются и сюда не попадают.
 */
function FlowChart({
  groups,
  currency,
  locale,
}: {
  groups: { label: string; fact: number; raw: number; weighted: number }[]
  currency: string | null
  locale: Locale
}) {
  const { t } = useApp()
  const max = Math.max(1, ...groups.flatMap((g) => [g.fact, g.raw, g.weighted]))
  const width = 560
  const base = 150
  const height = 120
  const bar = 44
  const label = (value: number) => moneyText(value, currency, locale)
  const aria = groups
    .map((g) => `${g.label}: ${t('fr.dds.factShort')} ${label(g.fact)}, ${t('fr.dds.rawShort')} ${label(g.raw)}, ${t('fr.dds.weightedShort')} ${label(g.weighted)}`)
    .join('. ')
  return (
    <div className="chart" role="img" aria-label={aria}>
      <svg viewBox={`0 0 ${width} 186`} aria-hidden="true">
        <line className="zero" x1="20" y1={base} x2={width - 10} y2={base} />
        {groups.map((group, gi) => {
          const x0 = 40 + gi * 270
          const bars = [
            { value: group.fact, cls: 'bar', style: undefined },
            { value: group.raw, cls: 's-fc', style: { fill: 'transparent' } },
            { value: group.weighted, cls: 's-fcw', style: { fill: 'transparent' } },
          ]
          return (
            <g key={group.label}>
              {bars.map((b, bi) => {
                const h = (b.value / max) * height
                const x = x0 + bi * (bar + 12)
                return (
                  <g key={bi}>
                    <rect className={b.cls} style={b.style} x={x} y={base - h} width={bar} height={Math.max(h, 0.5)} rx={2} />
                    <text className="val" x={x + bar / 2} y={base - h - 6} textAnchor="middle">
                      {formatNumber(Math.round(b.value / 1000), locale)}
                    </text>
                  </g>
                )
              })}
              <text className="lbl" x={x0 + (bar * 3 + 24) / 2} y={base + 18} textAnchor="middle">{group.label}</text>
            </g>
          )
        })}
        <text className="lbl" x={width - 10} y={180} textAnchor="end">{t('fr.dds.thousands', { currency: currency ?? '' })}</text>
      </svg>
    </div>
  )
}
